import {
    CommitmentEntity,
    InteractionEntity,
    NoteEntity,
} from '../../coaching-base/config';
import { CommitmentDiff } from '../../coaching-base/diff-decorator';
import {
    ApprovalItem,
    CoachingEventStatus,
    CommitmentApprovalDto,
    CommitmentDraftDto,
    CommitmentDto,
    FocusDto,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import { BehaviourItem } from '../../coaching-tags/models/behaviour.coaching.model';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { SubBehaviourItem } from '../../coaching-tags/models/subbehaviour.coaching.model';
import { FocusShallow } from '../../coaching-tags/tags/tags.model';
import { createAction, PayloadAction } from '../../store.model';
import { GoalsCollection } from '../goals-collection.model';

import { CloseContext } from './close-context.model';
import { CommitmentFormValue } from './commitment.form';

export enum ActionType {
    COACHING_COMMITMENT__ROOT__RELOAD = 'COACHING_COMMITMENT__ROOT__RELOAD',
    COACHING_COMMITMENT__ROOT__RELOAD_RESULT = 'COACHING_COMMITMENT__ROOT__RELOAD_RESULT',
    COACHING_COMMITMENT__ROOT__SET_TAG = 'COACHING_COMMITMENT__ROOT__SET_TAG',

    COACHING_COMMITMENT__ROOT__SAVE = 'COACHING_COMMITMENT__ROOT__SAVE',
    COACHING_COMMITMENT__ROOT__SAVE_RESULT = 'COACHING_COMMITMENT__ROOT__SAVE_RESULT',

    COACHING_COMMITMENT__ROOT__PUBLISH = 'COACHING_COMMITMENT__ROOT__PUBLISH',
    COACHING_COMMITMENT__ROOT__PUBLISH_RESULT = 'COACHING_COMMITMENT__ROOT__PUBLISH_RESULT',
    COACHING_COMMITMENT__ROOT__CLOSE_REASON = 'COACHING_COMMITMENT__ROOT__CLOSE_REASON',
    COACHING_COMMITMENT__ROOT__CLOSE = 'COACHING_COMMITMENT__ROOT__CLOSE',
    COACHING_COMMITMENT__ROOT__DISPUTE = 'COACHING_COMMITMENT__ROOT__DISPUTE',
    COACHING_COMMITMENT__ROOT__SIGN_OFF = 'COACHING_COMMITMENT__ROOT__SIGN_OFF',

    COACHING_COMMITMENT__ROOT__DISMISS_MESSAGE = 'COACHING_COMMITMENT__ROOT__DISMISS_MESSAGE',
    COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY = 'COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY',
    COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY_RESULT = 'COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY_RESULT',

    COACHING_COMMITMENT__ROOT__DELETE = 'COACHING_COMMITMENT__ROOT__DELETE',
    COACHING_COMMITMENT__ROOT__DELETE_RESULT = 'COACHING_COMMITMENT__ROOT__DELETE_RESULT',

    COACHING_COMMITMENT__ROOT__EDIT = 'COACHING_COMMITMENT__ROOT__EDIT',

    COACHING_COMMITMENT__ROOT__SEND_APPROVAL = 'COACHING_COMMITMENT__ROOT__SEND_APPROVAL',
    COACHING_COMMITMENT__ROOT__SEND_APPROVAL_RESULT = 'COACHING_COMMITMENT__ROOT__SEND_APPROVAL_RESULT',

    COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL = 'COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL',
    COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL_RESULT = 'COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL_RESULT',
}

export interface RootReloadAction {
    performerId: number;
    draftId?: string;
    approvalId?: number;
    commitmentId?: number;
    coachId?: number;

    /**
     * used to set the focus on a new commitment
     */
    focus?: FocusShallow;

    /**
     * used to modify the initial form by adding an unsaved linked observation
     */
    observationId?: number;

    goalMetricIdList?: number[];

    /**
     * to set isEditing
     */
    edit?: boolean;

    /**
     * load the most recent approval if it was done (explicitly approved/disputed).
     * this checks the draftAndApproval property of the commitment.
     * this only takes effect if commitmentId is specified.
     * this has no effect if the original commitment is being loaded
     */
    loadLastApprovalIfItWasDone?: boolean;
}

export const rootReload = createAction<ActionType, RootReloadAction>(
    ActionType.COACHING_COMMITMENT__ROOT__RELOAD,
);

export interface RootReloadResultAction extends RootReloadAction {
    behaviours?: BehaviourItem[];
    subBehaviours?: SubBehaviourItem[];
    metrics?: MetricItem[];
    coachingEvent?: CommitmentEntity;
    noteEntity?: NoteEntity;
    interactionEntity?: InteractionEntity;
    draft?: CommitmentDraftDto;
    approval?: CommitmentApprovalDto;
    commitment?: CommitmentDto;
    //
    goalsCollection?: GoalsCollection;
}

export const rootReloadResult = createAction<
    ActionType,
    RootReloadResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__RELOAD_RESULT);

/// //////////////////////

export interface RootSetTagAction {
    focus: FocusDto;
}

export const rootSetTag = createAction<ActionType, RootSetTagAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SET_TAG,
);

/// //////////////////////

export interface RootSaveAction {
    formValue: CommitmentFormValue;
    actions?: Array<PayloadAction<any, any>>;
    isGeneric?: boolean;
    activeMetricId?: number;
}

export const rootSave = createAction<ActionType, RootSaveAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SAVE,
);

export interface RootSaveResultAction extends RootSaveAction {
    draftId?: string;
    draft?: CommitmentDraftDto;
    commitment?: CommitmentDto;
    //
    goalsCollection?: GoalsCollection;
}

export const rootSaveResult = createAction<ActionType, RootSaveResultAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SAVE_RESULT,
);

/// //////////////////////

export interface RootPublishAction {
    open: boolean;
}

export const rootPublish = createAction<ActionType, RootPublishAction>(
    ActionType.COACHING_COMMITMENT__ROOT__PUBLISH,
);

export interface RootPublishResultAction extends RootPublishAction {
    diff?: CommitmentDiff;
}

export const rootPublishResult = createAction<
    ActionType,
    RootPublishResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__PUBLISH_RESULT);

/// //////////////////////

export interface RootCloseReasonAction {
    open: boolean;
}

export const rootCloseReason = createAction<ActionType, RootCloseReasonAction>(
    ActionType.COACHING_COMMITMENT__ROOT__CLOSE_REASON,
);

/// //////////////////////

export interface RootCloseAction {
    open: boolean;
    ctx?: CloseContext;
}

export const rootClose = createAction<ActionType, RootCloseAction>(
    ActionType.COACHING_COMMITMENT__ROOT__CLOSE,
);

/// //////////////////////

export interface RootDisputeAction {
    open: boolean;
}

export const rootDispute = createAction<ActionType, RootDisputeAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DISPUTE,
);

/// //////////////////////

export interface RootSignOffAction {
    open: boolean;
    ctx: CoachingEventStatus;
}

export const rootSignOff = createAction<ActionType, RootSignOffAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF,
);

/// //////////////////////

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RootDismissMessageAction {}

// no arg since there's only one
export const rootDismissMessage = createAction<
    ActionType,
    RootDismissMessageAction
>(ActionType.COACHING_COMMITMENT__ROOT__DISMISS_MESSAGE);

/// //////////////////////

export interface RootRetrieveObservationSummaryAction {
    observationId: number;
}

export const rootRetrieveObservationSummary = createAction<
    ActionType,
    RootRetrieveObservationSummaryAction
>(ActionType.COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY);

export interface RootRetrieveObservationSummaryResultAction
    extends RootRetrieveObservationSummaryAction {
    observation?: ObservationSummaryDto;
}

export const rootRetrieveObservationSummaryResult = createAction<
    ActionType,
    RootRetrieveObservationSummaryResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY_RESULT);

/// //////////////////////

export interface RootDeleteAction {
    draftId?: string;
    commitmentId?: number;

    /** actions to run if the operation succeeds */
    actions?: Array<PayloadAction<any, any>>;
}

export const rootDelete = createAction<ActionType, RootDeleteAction>(
    ActionType.COACHING_COMMITMENT__ROOT__DELETE,
);

export interface RootDeleteResultAction extends RootDeleteAction {
    success?: boolean;
}

export const rootDeleteResult = createAction<
    ActionType,
    RootDeleteResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__DELETE_RESULT);

/// //////////////////////

export interface RootEditAction {
    edit?: boolean;
}

export const rootEdit = createAction<ActionType, RootEditAction>(
    ActionType.COACHING_COMMITMENT__ROOT__EDIT,
);

/// //////////////////////

export interface RootSendApprovalAction {
    toUserId: number;
    nextStatus: CoachingEventStatus;
    approved?: boolean;
    closeReasonId?: number;
    closeReasonName?: string;
    actions?: Array<PayloadAction<any, any>>;
}

export const sendApproval = createAction<ActionType, RootSendApprovalAction>(
    ActionType.COACHING_COMMITMENT__ROOT__SEND_APPROVAL,
);

export interface RootSendApprovalResultAction extends RootSendApprovalAction {
    approval?: CommitmentApprovalDto;
    goalsCollection?: GoalsCollection;
}

export const sendApprovalResult = createAction<
    ActionType,
    RootSendApprovalResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__SEND_APPROVAL_RESULT);

/// //////////////////////

export interface RootSignOffApprovalAction {
    nextStatus: CoachingEventStatus;
    approved: boolean;
    items: ApprovalItem[];
    actions?: Array<PayloadAction<any, any>>;
}

export const rootSignOffApproval = createAction<
    ActionType,
    RootSignOffApprovalAction
>(ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL);

export interface RootSignOffApprovalResultAction
    extends RootSignOffApprovalAction {
    approval?: CommitmentApprovalDto;
    goalsCollection?: GoalsCollection;
}

export const rootSignOffApprovalResult = createAction<
    ActionType,
    RootSignOffApprovalResultAction
>(ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL_RESULT);
